import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, History, ArrowRightLeft, ExternalLink, Filter } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateTime } from "../lib/dateFormat";

/**
 * Feb 2026 · 10 Feb 2026 · Concierge-Historie (Audit-Viewer).
 *
 * User-Directive: „Neue Zeile im Admin-Sidebar unter 'Zugänge' →
 * 'Concierge-Historie' mit Filter nach Objekt + Actor."
 *
 * Backend: GET /api/team/concierge-reassign-audit (admin-only, sortiert
 * absteigend nach `at`, optional `?property_id=…`).
 * Client-Filter: Actor (Admin-Name/E-Mail) + Volltextsuche über Objekt-
 * und Concierge-Namen. Kombiniert mit dem Backend-Property-Filter für
 * eine nutzergesteuerte Doppel-Kaskade.
 */
export default function TeamConciergeHistory() {
  const [rows, setRows] = useState(null);
  const [properties, setProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/properties");
        setProperties((data || []).map((p) => ({ id: p.id, name: p.name || "—" }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      } catch { /* ignore */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (propertyFilter) params.set("property_id", propertyFilter);
      params.set("limit", "300");
      const { data } = await api.get(`/team/concierge-reassign-audit?${params}`);
      setRows(data || []);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyFilter]);
  useEffect(() => { load(); }, [load]);

  // Actor-Optionen aus geladenen Zeilen (deduped)
  const actorOptions = useMemo(() => {
    const set = new Map();
    for (const r of rows || []) {
      const key = r.actor_email || r.actor_name || r.actor_id;
      if (!key) continue;
      set.set(key, r.actor_name || r.actor_email || "—");
    }
    return Array.from(set.entries()).map(([k, v]) => ({ value: k, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filtered = useMemo(() => {
    let base = rows || [];
    if (actorFilter) {
      base = base.filter((r) => (r.actor_email || r.actor_name || r.actor_id) === actorFilter);
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      base = base.filter((r) =>
        (r.property_name || "").toLowerCase().includes(s)
        || (r.old_concierge_name || "").toLowerCase().includes(s)
        || (r.new_concierge_name || "").toLowerCase().includes(s)
        || (r.actor_name || "").toLowerCase().includes(s));
    }
    return base;
  }, [rows, actorFilter, q]);

  const clearFilters = () => { setPropertyFilter(""); setActorFilter(""); setQ(""); };
  const hasFilter = propertyFilter || actorFilter || q.trim();

  return (
    <div className="max-w-[1080px] mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="team-concierge-history">
      <div className="mb-6">
        <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Weissblom · Admin</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight flex items-center gap-3">
          <History className="w-7 h-7 text-navy-dark/50" strokeWidth={1.5} />
          Concierge-Historie
        </h1>
        <p className="mt-3 text-[13.5px] text-navy-dark/70 leading-snug max-w-[640px]">
          Vollständiges Audit-Log aller Haupt-Concierge-Wechsel: Wer hat wann welchem Objekt
          welchen neuen Verantwortlichen zugewiesen, wurde ein Cascade-Transfer offener Items
          ausgeführt und wie viele Elemente wurden übertragen.
        </p>
      </div>

      {/* Filter */}
      <div className="bg-white border border-navy/10 p-4 md:p-5 mb-6 space-y-3 md:space-y-0 md:flex md:items-end md:gap-3 md:flex-wrap">
        <label className="block md:min-w-[220px] md:flex-1">
          <span className="block text-[11px] uppercase tracking-wider text-navy-dark/60 mb-1">Objekt</span>
          <select
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            data-testid="history-filter-property"
            className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
          >
            <option value="">— Alle Objekte —</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="block md:min-w-[200px] md:flex-1">
          <span className="block text-[11px] uppercase tracking-wider text-navy-dark/60 mb-1">Actor (Admin)</span>
          <select
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            data-testid="history-filter-actor"
            className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
          >
            <option value="">— Alle Actors —</option>
            {actorOptions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </label>
        <label className="block md:min-w-[180px] md:flex-1">
          <span className="block text-[11px] uppercase tracking-wider text-navy-dark/60 mb-1">Volltext</span>
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Objekt / Concierge / Actor"
            data-testid="history-filter-q"
            className="w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
          />
        </label>
        {hasFilter && (
          <button type="button" onClick={clearFilters} data-testid="history-filter-clear"
                  className="inline-flex items-center gap-1.5 border border-navy/20 hover:bg-navy/5 px-3 py-2 text-[11px] tracking-wider uppercase">
            <Filter className="w-3.5 h-3.5" /> Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-navy/10 overflow-hidden">
        <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between text-[12px] text-navy-dark/60">
          <span data-testid="history-row-count">
            {filtered.length} {filtered.length === 1 ? "Eintrag" : "Einträge"}
            {rows && rows.length !== filtered.length && ` (von ${rows.length})`}
          </span>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gold" />}
        </div>
        {rows === null ? (
          <div className="p-8 text-center text-navy-dark/50 text-[13px]">Lade Audit-Log …</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-navy-dark/50 text-[13px]" data-testid="history-empty">
            {hasFilter ? "Keine Einträge mit diesen Filtern." : "Noch keine Concierge-Wechsel dokumentiert."}
          </div>
        ) : (
          <ul className="divide-y divide-navy/5">
            {filtered.map((r) => <Row key={r.id} row={r} />)}
          </ul>
        )}
      </div>
    </div>
  );
}

function Row({ row }) {
  const at = new Date(row.at);
  const stamp = deDateTime(at);
  const moved = row.moved || {};
  const movedParts = [
    moved.bookings && `${moved.bookings} Buchung${moved.bookings === 1 ? "" : "en"}`,
    moved.requests && `${moved.requests} Aufgabe${moved.requests === 1 ? "" : "n"}`,
    moved.projects && `${moved.projects} Projekt${moved.projects === 1 ? "" : "e"}`,
    moved.calendar_events && `${moved.calendar_events} Termin${moved.calendar_events === 1 ? "" : "e"}`,
  ].filter(Boolean);
  return (
    <li className="px-5 py-4 hover:bg-navy/[0.02] transition-colors" data-testid={`history-row-${row.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[13px] text-navy flex-wrap">
            <span className="font-medium">{row.property_name || "—"}</span>
            <span className="text-navy-dark/40">·</span>
            <span className="text-navy-dark/70">{row.old_concierge_name || "—"}</span>
            <ArrowRightLeft className="w-3.5 h-3.5 text-navy-dark/50" strokeWidth={1.7} />
            <span className="text-navy-dark/70">{row.new_concierge_name || "— (unassigned)"}</span>
          </div>
          <div className="mt-1 text-[11.5px] text-navy-dark/50">
            {stamp} · durch {row.actor_name || row.actor_email || "—"}
          </div>
          {row.cascade_applied && movedParts.length > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded"
                 style={{ background: withAlpha(STATUS_COLORS.blue, 0.08), color: STATUS_COLORS.blue }}
                 data-testid={`history-cascade-${row.id}`}>
              Cascade · {movedParts.join(" · ")}
            </div>
          )}
          {row.cascade_requested && !row.cascade_applied && (
            <div className="mt-1.5 inline-flex items-center text-[11px] px-2 py-0.5 rounded"
                 style={{ background: withAlpha(STATUS_COLORS.grey, 0.15), color: STATUS_COLORS.grey_ink }}>
              Nur Objekt-Wechsel · keine Items zu übertragen
            </div>
          )}
        </div>
        {row.property_id && (
          <a href={`/team/properties/${row.property_id}`}
             data-testid={`history-open-property-${row.id}`}
             className="text-[11px] tracking-wider uppercase text-navy border-b border-gold/60 hover:border-gold inline-flex items-center gap-1 pb-0.5">
            <ExternalLink className="w-3 h-3" /> Objekt öffnen
          </a>
        )}
      </div>
    </li>
  );
}
